import React, { ReactNode, useCallback, useId, useMemo, useState } from 'react';

import { Label } from '@swyftx/aviary/atoms/Label';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { SelectItemData } from '@swyftx/aviary/atoms/Select';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';

import { cn } from '@shared/utils/lib/ui';

import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';

import { Search } from './Query';
import { Trigger } from './Trigger';

type Props = {
  disabled?: boolean;
  errorMessage?: string;
  items: SelectItemData[];
  label: ReactNode;
  modalTitle: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  spotlightElementId?: string;
  triggerClassName?: string;
  value?: string;
};

const DropdownSearch: React.FC<Props> = ({
  disabled,
  errorMessage,
  items,
  label,
  modalTitle,
  onValueChange,
  placeholder,
  spotlightElementId,
  triggerClassName,
  value,
}) => {
  const [open, setOpen] = useState(false);
  const selectedItem = useMemo(() => items.find((it) => it.value === value), [items, value]);
  const isXs = useTailwindBreakpoint('xs');
  const id = useId();

  const handleValueChange = useCallback(
    (newValue: string) => {
      onValueChange(newValue);
      setOpen(false);
    },
    [onValueChange],
  );

  return (
    <FlexLayout className='gap-8' direction='column'>
      <Label htmlFor={id}>{label}</Label>

      {isXs ? (
        <Modal
          title={modalTitle}
          open={open}
          onClose={() => setOpen(false)}
          onOpenChange={(o) => setOpen(!!o)}
          triggerElement={
            <div>
              <Trigger
                open={open}
                isError={Boolean(errorMessage)}
                disabled={disabled}
                triggerClassName={triggerClassName}
                id={id}
                spotlightElementId={spotlightElementId}
                value={value}
                selectedItem={selectedItem}
                placeholder={placeholder}
              />
            </div>
          }
          showCloseButton
          position='bottom'
          className='z-modal-popover'
          overlayClassName='z-modal-popover-backdrop'
        >
          <Search isCompact={isXs} items={items} selectedItem={selectedItem} handleValueChange={handleValueChange} />
        </Modal>
      ) : (
        <FlexLayout direction='column'>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger>
              <Trigger
                open={open}
                isError={Boolean(errorMessage)}
                disabled={disabled}
                triggerClassName={triggerClassName}
                id={id}
                spotlightElementId={spotlightElementId}
                value={value}
                selectedItem={selectedItem}
                placeholder={placeholder}
              />
            </PopoverTrigger>
            <PopoverContent
              className={cn(
                'z-modal-popover w-[var(--radix-popover-trigger-width)]',
                'mt-6 rounded-12 shadow-md outline outline-1 outline-color-border-main',
              )}
            >
              <Search
                isCompact={isXs}
                items={items}
                selectedItem={selectedItem}
                handleValueChange={handleValueChange}
              />
            </PopoverContent>
          </Popover>
        </FlexLayout>
      )}

      {errorMessage && (
        <Body size='small' className='text-color-text-error'>
          {errorMessage}
        </Body>
      )}
    </FlexLayout>
  );
};

export { DropdownSearch };
